import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const FiscalYearContext = createContext();

export const useFiscalYear = () => {
  const context = useContext(FiscalYearContext);
  if (!context) {
    throw new Error('useFiscalYear must be used within a FiscalYearProvider');
  }
  return context;
};

export const FiscalYearProvider = ({ children }) => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [currentFiscalYear, setCurrentFiscalYear] = useState('');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch available fiscal years from backend
  const fetchFiscalYears = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await axios.get(`${backendUrl}/api/fiscal-years`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const { fiscal_years, current_fiscal_year } = response.data;
      
      setFiscalYears(fiscal_years);
      setCurrentFiscalYear(current_fiscal_year);
      
      // Set selected to current if not already set
      if (!selectedFiscalYear && current_fiscal_year) {
        setSelectedFiscalYear(current_fiscal_year);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching fiscal years:', error);
      setLoading(false);
    }
  };

  // Fetch fiscal years on mount only if token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchFiscalYears();
    } else {
      setLoading(false);
    }
  }, []);

  // Change selected fiscal year
  const changeFiscalYear = (fiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    // Store in localStorage for persistence
    localStorage.setItem('selectedFiscalYear', fiscalYear);
  };

  // Load selected fiscal year from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('selectedFiscalYear');
    if (stored && fiscalYears.includes(stored)) {
      setSelectedFiscalYear(stored);
    } else if (currentFiscalYear) {
      setSelectedFiscalYear(currentFiscalYear);
    }
  }, [fiscalYears, currentFiscalYear]);

  const value = {
    fiscalYears,
    currentFiscalYear,
    selectedFiscalYear,
    changeFiscalYear,
    loading,
    refreshFiscalYears: fetchFiscalYears,
    initializeFiscalYears: fetchFiscalYears
  };

  return (
    <FiscalYearContext.Provider value={value}>
      {children}
    </FiscalYearContext.Provider>
  );
};
